package org.example.project.ui.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.example.project.client.error.ApiClientException
import org.example.project.client.error.UnauthorizedException
import org.example.project.client.model.ActivityResponse
import org.example.project.client.model.ClientType
import org.example.project.client.model.LoginRequest
import org.example.project.client.session.SessionRepository
import org.example.project.client.session.SessionState
import org.example.project.ui.EditorNavigationState

data class LoginUiState(
    val email: String = "",
    val password: String = "",
    val isSubmitting: Boolean = false,
    val validationError: String? = null
)

data class EditorShellUiState(
    val navState: EditorNavigationState = EditorNavigationState.Loading,
    val sessionState: SessionState = SessionState.Unauthenticated,
    val statusMessage: String? = null,
    val errorMessage: String? = null,
    val isMobileMenuOpen: Boolean = false,
    val loginState: LoginUiState = LoginUiState()
)

class EditorShellViewModel(
    private val sessionRepository: SessionRepository
) : ViewModel() {

    private val _uiState = MutableStateFlow(
        EditorShellUiState(
            sessionState = sessionRepository.sessionState.value
        )
    )
    val uiState: StateFlow<EditorShellUiState> = _uiState.asStateFlow()

    init {
        // Observe repository session state changes
        viewModelScope.launch {
            sessionRepository.sessionState.collect { sessionState ->
                _uiState.update { current ->
                    val newNavState = when (sessionState) {
                        is SessionState.Unauthenticated -> {
                            if (current.navState !is EditorNavigationState.Loading) {
                                EditorNavigationState.Login
                            } else {
                                current.navState
                            }
                        }
                        is SessionState.AuthenticatedWeb -> {
                            if (sessionState.user.roles.contains("admin")) {
                                if (current.navState is EditorNavigationState.Login || current.navState is EditorNavigationState.Loading) {
                                    EditorNavigationState.ActivityList
                                } else {
                                    current.navState
                                }
                            } else {
                                if (current.navState is EditorNavigationState.Login || current.navState is EditorNavigationState.Loading) {
                                    EditorNavigationState.AccessDenied(sessionState.user)
                                } else if (current.navState is EditorNavigationState.ActivityList || current.navState is EditorNavigationState.ActivityForm) {
                                    EditorNavigationState.AccessDenied(sessionState.user)
                                } else {
                                    current.navState
                                }
                            }
                        }
                        else -> current.navState
                    }
                    current.copy(
                        sessionState = sessionState,
                        navState = newNavState
                    )
                }
            }
        }
    }

    fun restoreSession() {
        viewModelScope.launch {
            try {
                val restored = sessionRepository.restoreSession()
                _uiState.update { current ->
                    val newNav = when (restored) {
                        is SessionState.AuthenticatedWeb -> {
                            if (restored.user.roles.contains("admin")) {
                                EditorNavigationState.ActivityList
                            } else {
                                EditorNavigationState.AccessDenied(restored.user)
                            }
                        }
                        else -> EditorNavigationState.Login
                    }
                    current.copy(navState = newNav)
                }
            } catch (_: Throwable) {
                _uiState.update { it.copy(navState = EditorNavigationState.Login) }
            }
        }
    }

    fun onLoginEmailChange(newEmail: String) {
        _uiState.update { current ->
            current.copy(
                loginState = current.loginState.copy(
                    email = newEmail,
                    validationError = null
                )
            )
        }
    }

    fun onLoginPasswordChange(newPassword: String) {
        _uiState.update { current ->
            current.copy(
                loginState = current.loginState.copy(
                    password = newPassword,
                    validationError = null
                )
            )
        }
    }

    fun login() {
        val email = _uiState.value.loginState.email.trim()
        val password = _uiState.value.loginState.password

        if (email.isEmpty() || !email.contains("@")) {
            _uiState.update { current ->
                current.copy(
                    loginState = current.loginState.copy(
                        validationError = "Wprowadź prawidłowy adres e-mail"
                    )
                )
            }
            return
        }

        if (password.length < 8) {
            _uiState.update { current ->
                current.copy(
                    loginState = current.loginState.copy(
                        validationError = "Hasło musi mieć co najmniej 8 znaków"
                    )
                )
            }
            return
        }

        _uiState.update { current ->
            current.copy(
                errorMessage = null,
                loginState = current.loginState.copy(
                    isSubmitting = true,
                    validationError = null
                )
            )
        }

        viewModelScope.launch {
            try {
                val result = sessionRepository.login(
                    LoginRequest(
                        email = email,
                        password = password,
                        clientType = ClientType.WEB
                    )
                )
                // Always clear entered password immediately after submission
                if (result is SessionState.AuthenticatedWeb) {
                    if (result.user.roles.contains("admin")) {
                        _uiState.update { current ->
                            current.copy(
                                navState = EditorNavigationState.ActivityList,
                                statusMessage = "Zalogowano pomyślnie jako ${result.user.displayName}",
                                loginState = LoginUiState()
                            )
                        }
                    } else {
                        _uiState.update { current ->
                            current.copy(
                                navState = EditorNavigationState.AccessDenied(result.user),
                                statusMessage = "Zalogowano pomyślnie jako ${result.user.displayName}",
                                loginState = LoginUiState()
                            )
                        }
                    }
                } else {
                    _uiState.update { current ->
                        current.copy(
                            loginState = current.loginState.copy(isSubmitting = false, password = "")
                        )
                    }
                }
            } catch (e: UnauthorizedException) {
                _uiState.update { current ->
                    current.copy(
                        errorMessage = "Błędny e-mail lub hasło (lub konto jest nieaktywne)",
                        loginState = current.loginState.copy(isSubmitting = false, password = "")
                    )
                }
            } catch (e: ApiClientException) {
                _uiState.update { current ->
                    current.copy(
                        errorMessage = "Błąd logowania: ${e.message}",
                        loginState = current.loginState.copy(isSubmitting = false, password = "")
                    )
                }
            } catch (e: Throwable) {
                _uiState.update { current ->
                    current.copy(
                        errorMessage = "Błąd połączenia z serwerem: ${e.message}",
                        loginState = current.loginState.copy(isSubmitting = false, password = "")
                    )
                }
            }
        }
    }

    fun logout() {
        viewModelScope.launch {
            try {
                sessionRepository.logout()
            } catch (_: Throwable) {}
            _uiState.update { current ->
                current.copy(
                    navState = EditorNavigationState.Login,
                    loginState = LoginUiState()
                )
            }
        }
    }

    fun dismissStatus() {
        _uiState.update { it.copy(statusMessage = null) }
    }

    fun dismissError() {
        _uiState.update { it.copy(errorMessage = null) }
    }

    fun showStatus(message: String) {
        _uiState.update { it.copy(statusMessage = message) }
    }

    fun showError(message: String) {
        _uiState.update { it.copy(errorMessage = message) }
    }

    fun toggleMobileMenu() {
        _uiState.update { it.copy(isMobileMenuOpen = !it.isMobileMenuOpen) }
    }

    fun closeMobileDrawer() {
        _uiState.update { it.copy(isMobileMenuOpen = false) }
    }

    fun navigateToList() {
        val session = _uiState.value.sessionState
        if (session is SessionState.AuthenticatedWeb && !session.user.roles.contains("admin")) {
            _uiState.update { it.copy(navState = EditorNavigationState.AccessDenied(session.user)) }
        } else {
            _uiState.update { it.copy(navState = EditorNavigationState.ActivityList) }
        }
    }

    fun navigateToNew() {
        val session = _uiState.value.sessionState
        if (session is SessionState.AuthenticatedWeb && !session.user.roles.contains("admin")) {
            _uiState.update { it.copy(navState = EditorNavigationState.AccessDenied(session.user)) }
        } else {
            _uiState.update { it.copy(navState = EditorNavigationState.ActivityForm(null)) }
        }
    }

    fun navigateToPlanner() {
        _uiState.update { it.copy(navState = EditorNavigationState.Planner) }
    }

    fun navigateToEdit(activity: ActivityResponse) {
        _uiState.update { it.copy(navState = EditorNavigationState.ActivityForm(activity)) }
    }

    fun navigateToLogin() {
        _uiState.update { it.copy(navState = EditorNavigationState.Login) }
    }
}
